// File viewer page: /view/{file_id} shows images, PDF, markdown and
// text / code in the browser (v1-scope "File preview"). Files the
// viewer cannot show, or that are too large for it, get a download
// button instead; office documents also offer the configured editor.

import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Download } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { requestDownloadUrl, useFile, useFileContent } from "@/api/queries";
import type { FileEntry } from "@/api/types";
import { useAuth } from "@/auth/stub";
import { AuthGate } from "@/components/AuthGate";
import { CheckoutBanner } from "@/components/CheckoutLine";
import { FileViewer } from "@/components/FileViewer";
import { OpenInOfficeButton } from "@/components/OpenInOfficeButton";
import { Button } from "@/components/ui/button";
import { formatBytes } from "@/lib/format";
import { fitsPreview, previewKind } from "@/lib/preview";

export const Route = createFileRoute("/view/$fileId")({
  component: function ViewPage() {
    return (
      <AuthGate>
        <ViewContent />
      </AuthGate>
    );
  },
});

async function download(file: FileEntry): Promise<void> {
  try {
    const { url } = await requestDownloadUrl(file.id);
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err) {
    console.error("download failed", err);
  }
}

/** Where "back" goes: the file's folder for its owner, else the shared list. */
function backTarget(file: FileEntry | undefined, userId: string | null) {
  if (file === undefined || file.owner_id !== userId) {
    return file === undefined
      ? ({ to: "/" } as const)
      : ({ to: "/shared-with-me" } as const);
  }
  return file.parent_id
    ? ({
        to: "/folder/$folderId",
        params: { folderId: file.parent_id },
      } as const)
    : ({ to: "/" } as const);
}

function ViewContent() {
  const { t } = useTranslation();
  const { fileId } = Route.useParams();
  const navigate = useNavigate();
  const userId = useAuth((s) => s.user?.id ?? null);
  const file = useFile(fileId);
  const entry = file.data;
  const kind = entry ? previewKind(entry.name, entry.content_type) : null;
  const fits = kind !== null && fitsPreview(kind, entry?.size);
  const content = useFileContent(entry, fits);
  const back = backTarget(entry, userId);
  const heading = useRef<HTMLHeadingElement>(null);

  // Focus the file name once it is known, so screen readers announce
  // the page; Escape goes back to where the file was opened from.
  const entryId = entry?.id;
  useEffect(() => {
    if (entryId) heading.current?.focus();
  }, [entryId]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) void navigate(back);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, back]);

  if (file.isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {file.error instanceof Error ? file.error.message : t("files.error")}
      </p>
    );
  }
  if (entry === undefined) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {t("files.loading")}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <Button asChild variant="ghost" size="icon">
            <Link {...back} aria-label={t("viewer.back", "Back to folder")}>
              <ArrowLeft className="h-4 w-4" aria-hidden />
            </Link>
          </Button>
          <h1
            ref={heading}
            tabIndex={-1}
            className="truncate text-lg font-semibold outline-none"
          >
            {entry.name}
          </h1>
          <span className="shrink-0 text-sm text-muted-foreground">
            {formatBytes(entry.size)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <OpenInOfficeButton file={entry} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => void download(entry)}
          >
            <Download className="mr-1 h-4 w-4" aria-hidden />
            {t("viewer.download", "Download")}
          </Button>
        </div>
      </div>
      {entry.checkout && (
        <CheckoutBanner
          checkout={entry.checkout}
          selfId={userId ?? undefined}
        />
      )}
      {kind === null ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t(
            "viewer.binary",
            "This file does not contain text that can be shown. Download it to open it.",
          )}
        </p>
      ) : !fits ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t(
            "viewer.too_large",
            "This file is too large to preview ({{size}}). Download it to open it.",
            { size: formatBytes(entry.size) },
          )}
        </p>
      ) : content.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {t("viewer.error", "The preview could not be loaded.")}
        </p>
      ) : content.data === undefined ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t("viewer.loading", "Loading preview…")}
        </p>
      ) : (
        <FileViewer file={entry} kind={kind} bytes={content.data} />
      )}
    </div>
  );
}
